import type { ApiError } from './http';
import { ApiErrorCodes } from './errors';
import type { ApiErrorCodeLike } from './errors';
import type { SchedulingApi } from './contract';
import type {
  Account,
  Draft,
  DraftInput,
  Folder,
  MessageFlag,
  MessageSummary,
  ScheduledSend,
  SchedulingStatus,
  SendResult,
  Snooze,
} from './types';

/**
 * MOCK: Send later and Snooze (ADR 0076), for `VITE_USE_MOCKS=true` and tests.
 *
 * It keeps the server's rules where the UI has to meet them: nothing is
 * scheduled or snoozed without the stored background credential (409
 * `stored_credential_required`, the same details the handler sends), a time
 * must be in the future and at most 366 days ahead (400, `field`), and a job
 * already sending answers 409. Because the mock starts with background
 * notifications off, the first schedule or snooze of a session is refused
 * until the enrolment endpoint is called, exactly as on a fresh server.
 *
 * There is no scheduler: a job whose time has passed is finished lazily the
 * next time its list is read, which is enough for mock mode to look alive.
 */

/** The consent version a new enrolment is made under (ADR 0076 §2); any enrolment qualifies. */
const SCHEDULING_CONSENT_VERSION = 4;
const MAX_DAYS_AHEAD = 366;
const DAY_MS = 86_400_000;
const MAX_SNOOZE_IDS = 100;

/** The live store the mock keeps: arrays may be REPLACED, so always read them through it. */
export interface MockSchedulingStore {
  accounts: Account[];
  folders: Folder[];
  messages: MessageSummary[];
  drafts: Draft[];
}

export interface MockSchedulingDeps {
  store: MockSchedulingStore;
  delay: (signal?: AbortSignal) => Promise<void>;
  newId: (prefix: string) => string;
  mockError: (status: number, code: ApiErrorCodeLike, message: string, details?: Record<string, unknown>) => ApiError;
  /** Where the mailbox's background enrolment stands. */
  enrolment: (account: Account) => 'on' | 'off' | 'needs_reconsent';
  /** Files the message into Sent, as `compose.sendDirect` does. */
  deliver: (accountId: string, input: DraftInput, messageId: string) => SendResult;
  /** A folder id, as the fixtures spell one. */
  folderId: (accountId: string, path: string) => string;
  /** Recounts the folders after a move. */
  resync: () => void;
}

interface ScheduledJob extends ScheduledSend {
  input: DraftInput;
}

interface SnoozeJob extends Snooze {
  /** The flags it had going in, restored by an unsnooze. */
  flags: MessageFlag[];
}

export function createMockSchedulingApi(deps: MockSchedulingDeps): SchedulingApi {
  const { store, delay, newId, mockError } = deps;
  let scheduled: ScheduledJob[] = [];
  let snoozes: SnoozeJob[] = [];

  const notFound = (kind: string, id: string): ApiError =>
    mockError(404, ApiErrorCodes.NotFound, `${kind} "${id}" was not found.`);

  const requireAccount = (accountId: string): Account => {
    const account = store.accounts.find((a) => a.id === accountId);
    if (account === undefined) throw notFound('Account', accountId);
    return account;
  };

  const statusOf = (account: Account): SchedulingStatus => {
    const enrolment = deps.enrolment(account);
    return {
      available: enrolment === 'on',
      reason: enrolment === 'on' ? '' : enrolment === 'needs_reconsent' ? 'needs_reconsent' : 'not_enrolled',
      kind: account.isPrimary ? 'primary' : 'connected',
      consentVersion: SCHEDULING_CONSENT_VERSION,
    };
  };

  /** 409 `stored_credential_required` before anything is written (ADR 0076 §1). */
  const requireCredential = (account: Account): void => {
    const status = statusOf(account);
    if (status.available) return;
    throw mockError(
      409,
      ApiErrorCodes.StoredCredentialRequired,
      'This mailbox needs background notifications turned on before the server can act on it on its own.',
      {
        reason: status.reason,
        account_id: account.id,
        kind: status.kind,
        consent_version: SCHEDULING_CONSENT_VERSION,
      },
    );
  };

  /** A future instant at most 366 days ahead, or 400 naming the field. */
  const requireWhen = (value: string, field: 'send_at' | 'wake_at'): number => {
    const at = Date.parse(value);
    const now = Date.now();
    if (Number.isNaN(at) || !/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      throw mockError(400, ApiErrorCodes.BadRequest, `${field} must be an RFC 3339 time.`, { field });
    }
    if (at <= now) throw mockError(400, ApiErrorCodes.BadRequest, `${field} must be in the future.`, { field });
    if (at > now + MAX_DAYS_AHEAD * DAY_MS) {
      throw mockError(400, ApiErrorCodes.BadRequest, `${field} must be within ${MAX_DAYS_AHEAD} days.`, { field });
    }
    return at;
  };

  /** The role folder, created at the top level on first use as the server does. */
  const roleFolder = (accountId: string, role: 'scheduled' | 'snoozed', create: boolean): Folder | undefined => {
    const existing = store.folders.find((f) => f.accountId === accountId && f.role === role);
    if (existing !== undefined || !create) return existing;
    const name = role === 'scheduled' ? 'Scheduled' : 'Snoozed';
    const inbox = store.folders.find((f) => f.accountId === accountId && f.role === 'inbox');
    const folder: Folder = {
      id: deps.folderId(accountId, name),
      accountId,
      path: name,
      name,
      parentId: null,
      role,
      roleSource: 'heuristic',
      delimiter: inbox?.delimiter ?? '/',
      unreadCount: 0,
      totalCount: 0,
      selectable: true,
      subscribed: true,
      uidValidity: 1,
    };
    store.folders.push(folder);
    return folder;
  };

  const removeMessage = (accountId: string, messageId: string | null): void => {
    if (messageId === null) return;
    store.messages = store.messages.filter((m) => !(m.accountId === accountId && m.id === messageId));
  };

  const publicJob = (job: ScheduledJob): ScheduledSend => {
    const { input: _input, ...rest } = job;
    return { ...rest, to: rest.to.map((a) => ({ ...a })) };
  };

  const publicSnooze = (job: SnoozeJob): Snooze => {
    const { flags: _flags, ...rest } = job;
    return { ...rest, from: rest.from ? { ...rest.from } : null };
  };

  const requireJob = (accountId: string, jobId: string): ScheduledJob => {
    const job = scheduled.find((j) => j.id === jobId && j.accountId === accountId);
    if (job === undefined) throw notFound('Scheduled send', jobId);
    return job;
  };

  const requireSnooze = (accountId: string, jobId: string): SnoozeJob => {
    const job = snoozes.find((j) => j.id === jobId && j.accountId === accountId);
    if (job === undefined) throw notFound('Snooze', jobId);
    return job;
  };

  /** Sends it: into Sent, the copy out of Scheduled, the job gone. */
  const finish = (job: ScheduledJob): SendResult => {
    const result = deps.deliver(job.accountId, job.input, newId('msg-sent'));
    removeMessage(job.accountId, job.messageId);
    scheduled = scheduled.filter((j) => j !== job);
    deps.resync();
    return result;
  };

  /** Back where it came from (INBOX when that folder is gone). */
  const wake = (job: SnoozeJob, returned: boolean): { messageId: string | null; folderId: string | null } => {
    const message = store.messages.find((m) => m.accountId === job.accountId && m.id === job.messageId);
    snoozes = snoozes.filter((j) => j !== job);
    if (message === undefined) return { messageId: null, folderId: null };
    const original =
      store.folders.find((f) => f.accountId === job.accountId && f.id === job.originalFolderId) ??
      store.folders.find((f) => f.accountId === job.accountId && f.role === 'inbox');
    if (original !== undefined) message.folderId = original.id;
    if (returned) {
      message.flags = [...message.flags.filter((flag) => flag !== 'seen' && flag !== 'snooze_returned'), 'snooze_returned'];
    } else {
      message.flags = [...job.flags];
    }
    deps.resync();
    return { messageId: message.id, folderId: message.folderId };
  };

  return {
    async status(accountId, signal) {
      await delay(signal);
      return statusOf(requireAccount(accountId));
    },

    async schedule(accountId, { sendAt, draftId, ...input }) {
      await delay();
      const account = requireAccount(accountId);
      requireCredential(account);
      const at = requireWhen(sendAt, 'send_at');
      if (input.to.length + input.cc.length + input.bcc.length === 0) {
        throw mockError(400, ApiErrorCodes.BadRequest, 'At least one recipient is required.');
      }
      const folder = roleFolder(accountId, 'scheduled', true)!;
      const copyId = newId('msg-scheduled');
      const now = new Date().toISOString();
      store.messages.push({
        id: copyId,
        accountId,
        folderId: folder.id,
        threadId: `thr-${copyId}`,
        subject: input.subject,
        from: { name: account.displayName, email: input.fromAddress },
        to: input.to.map((a) => ({ ...a })),
        date: now,
        snippet: (input.bodyText ?? input.bodyHtml.replace(/<[^>]+>/g, ' ')).trim().slice(0, 140),
        flags: ['seen', 'draft'],
        hasAttachments: input.attachmentIds.length > 0,
        sizeBytes: 2_048 + input.bodyHtml.length,
      });
      // As the server does: the composer's draft is retired once the copy is safe.
      if (draftId !== undefined) {
        store.drafts = store.drafts.filter((d) => !(d.id === draftId && d.accountId === accountId));
      }
      const job: ScheduledJob = {
        id: newId('sched'),
        accountId,
        state: 'scheduled',
        sendAt: new Date(at).toISOString(),
        attempts: 0,
        nextAttemptAt: null,
        errorCode: '',
        error: '',
        messageId: copyId,
        rfcMessageId: `${copyId}@dao-mail.com`,
        subject: input.subject,
        to: input.to.map((a) => ({ ...a })),
        hasAttachments: input.attachmentIds.length > 0,
        createdAt: now,
        input: { ...input },
      };
      scheduled.push(job);
      deps.resync();
      return publicJob(job);
    },

    async listScheduled(accountId, signal) {
      await delay(signal);
      requireAccount(accountId);
      const now = Date.now();
      for (const job of scheduled.filter((j) => j.accountId === accountId && j.state === 'scheduled')) {
        if (Date.parse(job.sendAt) <= now) finish(job);
      }
      return {
        items: scheduled
          .filter((j) => j.accountId === accountId)
          .sort((a, b) => Date.parse(a.sendAt) - Date.parse(b.sendAt))
          .map(publicJob),
        unscheduled: [],
        folderId: roleFolder(accountId, 'scheduled', false)?.id ?? null,
      };
    },

    async sendNow(accountId, jobId, options) {
      await delay();
      requireAccount(accountId);
      const job = requireJob(accountId, jobId);
      if (job.state === 'sending') throw mockError(409, ApiErrorCodes.Conflict, 'This message is already being sent.');
      if (job.errorCode === 'send_uncertain' && options?.confirmUncertain !== true) {
        throw mockError(409, ApiErrorCodes.Conflict, 'This message may already have been sent.', {
          reason: 'send_uncertain',
        });
      }
      const sending = publicJob({ ...job, state: 'sending', attempts: job.attempts + 1 });
      const result = finish(job);
      return { job: sending, result };
    },

    async cancelScheduled(accountId, jobId) {
      await delay();
      requireAccount(accountId);
      const job = requireJob(accountId, jobId);
      if (job.state === 'sending') throw mockError(409, ApiErrorCodes.Conflict, 'This message is already being sent.');
      const draft: Draft = { ...job.input, accountId, id: newId('draft'), updatedAt: new Date().toISOString() };
      store.drafts.push(draft);
      removeMessage(accountId, job.messageId);
      scheduled = scheduled.filter((j) => j !== job);
      deps.resync();
      return { job: publicJob(job), draftId: draft.id };
    },

    async snooze(accountId, { messageIds, wakeAt }) {
      await delay();
      const account = requireAccount(accountId);
      if (messageIds.length === 0 || messageIds.length > MAX_SNOOZE_IDS) {
        throw mockError(400, ApiErrorCodes.BadRequest, `message_ids must name 1 to ${MAX_SNOOZE_IDS} messages.`, {
          field: 'message_ids',
        });
      }
      requireCredential(account);
      const at = new Date(requireWhen(wakeAt, 'wake_at')).toISOString();
      const folder = roleFolder(accountId, 'snoozed', true)!;
      const items: Snooze[] = [];
      const failed: Array<{ messageId: string; code: string; message: string }> = [];
      for (const messageId of messageIds) {
        const message = store.messages.find((m) => m.accountId === accountId && m.id === messageId);
        if (message === undefined) {
          failed.push({ messageId, code: 'not_found', message: `Message "${messageId}" was not found.` });
          continue;
        }
        const existing = snoozes.find((j) => j.accountId === accountId && j.messageId === messageId);
        if (existing !== undefined) {
          // Already snoozed: re-timed, not snoozed twice.
          existing.wakeAt = at;
          items.push(publicSnooze(existing));
          continue;
        }
        const job: SnoozeJob = {
          id: newId('snooze'),
          accountId,
          state: 'snoozed',
          wakeAt: at,
          originalFolderId: message.folderId,
          messageId: message.id,
          rfcMessageId: `${message.id}@dao-mail.com`,
          subject: message.subject,
          from: { ...message.from },
          date: message.date,
          errorCode: '',
          error: '',
          createdAt: new Date().toISOString(),
          flags: [...message.flags],
        };
        message.folderId = folder.id;
        snoozes.push(job);
        items.push(publicSnooze(job));
      }
      deps.resync();
      return { items, failed };
    },

    async listSnoozes(accountId, signal) {
      await delay(signal);
      requireAccount(accountId);
      const now = Date.now();
      for (const job of snoozes.filter((j) => j.accountId === accountId && j.state === 'snoozed')) {
        if (Date.parse(job.wakeAt) <= now) wake(job, true);
      }
      return {
        items: snoozes
          .filter((j) => j.accountId === accountId)
          .sort((a, b) => Date.parse(a.wakeAt) - Date.parse(b.wakeAt))
          .map(publicSnooze),
        folderId: roleFolder(accountId, 'snoozed', false)?.id ?? null,
      };
    },

    async unsnooze(accountId, jobId) {
      await delay();
      requireAccount(accountId);
      const job = requireSnooze(accountId, jobId);
      const where = wake(job, false);
      return { job: publicSnooze(job), ...where };
    },
  };
}
